/**
 * useMusicPreview — a tiny module-scope audio singleton + React hook so
 * a single track can play across components (the soundtrack dropdown,
 * the mini-player, etc.) without restarting on dropdown open/close.
 *
 * Only one track can ever play at a time. Calls to `play(track)` while
 * a different track is playing will pause the old one cleanly first.
 *
 * The hook intentionally exposes only `playing`, `play(track)`, `pause()`,
 * and `toggle(track)` — no full-player APIs. Per product brief this is
 * a calm preview, not a music player.
 */
import { useEffect, useState } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

let audioEl = null; // singleton Audio
let currentTrack = null; // { track_id, name, mood, preview_url, ... } | null
const listeners = new Set();

function emit() {
  for (const l of listeners) {
    try { l(currentTrack); } catch { /* ignore */ }
  }
}

function _stop() {
  if (audioEl) {
    try { audioEl.pause(); } catch { /* ignore */ }
    audioEl = null;
  }
  if (currentTrack !== null) {
    currentTrack = null;
    emit();
  }
}

function _play(track) {
  if (!track || !track.preview_url) {
    _stop();
    return;
  }
  // If the same track is already playing, no-op
  if (currentTrack && currentTrack.track_id === track.track_id && audioEl && !audioEl.paused) {
    return;
  }
  // Pause any previous track first
  if (audioEl) {
    try { audioEl.pause(); } catch { /* ignore */ }
    audioEl = null;
  }
  const url = `${BACKEND}${track.preview_url}`;
  const a = new Audio(url);
  a.volume = 0.7;
  a.onended = () => {
    if (audioEl === a) {
      audioEl = null;
      currentTrack = null;
      emit();
    }
  };
  a.onerror = () => {
    if (audioEl === a) {
      audioEl = null;
      currentTrack = null;
      emit();
    }
  };
  a.play().catch(() => {
    if (audioEl === a) {
      audioEl = null;
      currentTrack = null;
      emit();
    }
  });
  audioEl = a;
  currentTrack = track;
  emit();
}

export function useMusicPreview() {
  const [playing, setPlaying] = useState(currentTrack);

  useEffect(() => {
    const fn = (t) => setPlaying(t);
    listeners.add(fn);
    // Sync initial state in case audio already playing in another mount
    setPlaying(currentTrack);
    return () => {
      listeners.delete(fn);
    };
  }, []);

  return {
    playing,
    play: _play,
    pause: _stop,
    toggle: (track) => {
      if (currentTrack && currentTrack.track_id === track.track_id) {
        _stop();
      } else {
        _play(track);
      }
    },
  };
}
